import { type DocumentData } from 'core/types'
import { sendGet } from 'hooks/use-fetch'
import { useEffect, useState } from 'react'
import { Grid } from '@mui/material'
import { RadiusCardClickable } from 'Components/Cards/Radius/RadiusCardClickable'
import { RadiusCard } from 'Components/Cards/Radius/RadiusCard'
import { FormatCurrency, FormatDateValue } from 'core/Utilities'
import { IncludeDocument } from 'Components/DocumentList/IncludeDocument'
import checkmark from 'Assets/Icons/checkmark.png'
import { useAuth } from 'hooks/use-auth'

interface DocumentSelectionProps {
  referenceType: string
  referenceID: number
  documentTypeID: number
  incomeRelated: boolean
  onInclude: (item: DocumentData[]) => void
  years: number[]
}

export function GeneralDocumentSelection (props: DocumentSelectionProps): JSX.Element {
  const [documents, setDocuments] = useState<DocumentData[]>([])
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null)
  const [includedDocuments, setIncludedDocuments] = useState<DocumentData[]>([])
  const auth = useAuth()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/Document/AvailableDocuments?referenceType=${props.referenceType}` +
        `&referenceId=${props.referenceID}&documentTypeID=${props.documentTypeID}`, {})
      if (success) {
        setDocuments(response as DocumentData[])
      }
    }
    void fetchData()
  }, [])

  const selectDocument = (item: DocumentData): void => {
    if (includedDocuments.findIndex(f => f.documentID === item.documentID) === -1) {
      const existing = documents.find(f => f.documentID === item.documentID)
      if (item.verified && (existing != null)) {
        const updatedDocuments = [...documents.filter(d => d.documentID !== item.documentID), existing]
        setDocuments(updatedDocuments)

        const newVals = [...includedDocuments, item]
        setIncludedDocuments(newVals)
        props.onInclude(newVals)
      } else {
        setSelectedDocument(item)
      }
    } else {
      setSelectedDocument(null)
    }
  }

  const includeDocument = (item: DocumentData): void => {
    let foundDoc = documents.find(f => f.documentID === item.documentID)
    if (foundDoc === undefined) { foundDoc = item }
    const existing = { ...foundDoc }
    if (existing !== undefined) {
      existing.verified = true
      existing.verifiedDate = new Date()
      existing.verificationNote = item.verificationNote
      existing.caregivers = item.caregivers
      if (auth.user != null) { existing.verifiedBy = auth.user.firstName + ' ' + auth.user.lastName }

      const updatedDocuments = [...documents.filter(d => d.documentID !== item.documentID), existing]
      setDocuments(updatedDocuments)
      const newVals = [...includedDocuments, existing]
      setIncludedDocuments(newVals)
      props.onInclude(newVals)
    }
    setSelectedDocument(null)
  }

  return <table className='ml-5' >
    {documents.length === 0 && <tr>
      <td colSpan={2} className='pl-25' data-testid='noDocsMessage'>No documents available to include</td>
    </tr>
    }
    {documents.length !== 0 && <>
      <tr>
        <td colSpan={2} className='pl-25'>Click on documents on the left and select whether to include them.</td>
      </tr>
      <tr>
        <td className='width-45' style={{ verticalAlign: 'top' }}>
          {documents.sort((a, b) => {
            if (a.documentID > b.documentID) { return -1 } else { return 1 }
          }).map((d) => {
            return <RadiusCardClickable
              key={d.documentID}
              icon={includedDocuments.findIndex(f => f.documentID === d.documentID) === -1 ? undefined : <img data-testid='selectedIcon' src={checkmark} alt='This document has been selected'/> }
              headerClassName='width-245'
              header={d.fileName}
              width={'100%'}
              dataTestId={`documentCard${d.documentID}`}
              contentClassName={selectedDocument !== null && selectedDocument.documentID === d.documentID ? 'card-selected' : ''}
              content={
                <Grid container direction='column' key={d.documentID.toString() + 'grid'}>
                  {props.incomeRelated && <Grid container direction='row' spacing={1} paddingBottom={1}>
                    <Grid item>Document Income:</Grid>
                    <Grid item data-testid='incomeDisplay'>{FormatCurrency(d.incomeAmount)}</Grid>
                  </Grid>
                  }
                  {props.incomeRelated && <Grid container direction='row' spacing={1} paddingBottom={1}>
                    <Grid item>Tax Year:</Grid>
                    <Grid item>{d.incomeYear}</Grid>
                  </Grid>
                  }
                  {
                    d.caregivers.map((val) => {
                      return <Grid key={d.documentID.toString() + 'griditem' + val.caregiverName} item>{val.caregiverName}</Grid>
                    })
                  }
                  {d.verified && <Grid item paddingTop={1}>Verified on {FormatDateValue(d.verifiedDate)}</Grid>}
                  {d.verified && <Grid item >{d.verificationNote}</Grid>}
                </Grid>
              }
              onClick={() => { selectDocument(d) }}
              className='mt-5'
            />
          })
          }
        </td>
        <td style={{ verticalAlign: 'top' }}>
          {selectedDocument !== null && <RadiusCard
            className='ml-10 width-275'
            dataTestId='includeDocumentCard'
            contentClassName='font-reducer'
            header={selectedDocument.fileName}
            content={<IncludeDocument documentID={selectedDocument.documentID} childID={selectedDocument.childID ?? 0} onInclude={includeDocument} years={props.years} />}
          />
          }
        </td>
      </tr>
    </>
    }
  </table >
}
